import { useEffect, useMemo, useRef, useState } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { ComposeRowData } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/NewPostModal/Types'
import { getVideoScriptRequestParams, isVideoScriptCategory } from 'utils/videoScriptRequest'
import { postGenerateVideoScript } from './useGenerateVideoScript'
import { clearVoiceoverScriptSession, ensureVoiceoverScriptSession } from './voiceoverScriptSession'

export const useVideoScriptSupport = ({
  brandPlanScheduleId,
  row,
}: {
  brandPlanScheduleId?: number
  row: ComposeRowData
}) => {
  const workspaceId = useCurrentWorkspaceId()
  const websocketConnection = useWebsocketConnection()
  const [supported, setSupported] = useState<boolean | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const activeTokenRef = useRef<string | null>(null)

  const { contentCategory, topic, tactics, postType, postAt, mediaFileIds, isBlank, loading } = row

  const requestParams = useMemo(() => {
    if (!brandPlanScheduleId) return null
    return getVideoScriptRequestParams(brandPlanScheduleId, { contentCategory, topic, tactics, postType, postAt, mediaFileIds })
  }, [brandPlanScheduleId, contentCategory, topic, tactics, postType, postAt, mediaFileIds])

  const shouldCheck =
    !!brandPlanScheduleId &&
    !!requestParams &&
    !isBlank &&
    !loading &&
    isVideoScriptCategory(contentCategory)

  useEffect(() => {
    if (!shouldCheck || !requestParams) {
      if (activeTokenRef.current) {
        clearVoiceoverScriptSession(activeTokenRef.current)
        activeTokenRef.current = null
      }
      setSupported(null)
      setToken(null)
      setIsChecking(false)
      return
    }

    let cancelled = false
    setIsChecking(true)
    setSupported(null)
    setToken(null)

    void postGenerateVideoScript(workspaceId, requestParams)
      .then((response) => {
        if (cancelled) return
        setSupported(response.supported)

        if (response.supported && response.token) {
          setToken(response.token)
          activeTokenRef.current = response.token
          return
        }

        setToken(null)
      })
      .catch(() => {
        if (cancelled) return
        setSupported(false)
        setToken(null)
      })
      .finally(() => {
        if (!cancelled) setIsChecking(false)
      })

    return () => {
      cancelled = true
      if (activeTokenRef.current) {
        clearVoiceoverScriptSession(activeTokenRef.current)
        activeTokenRef.current = null
      }
    }
  }, [shouldCheck, workspaceId, requestParams])

  useEffect(() => {
    if (!token || !websocketConnection) return
    ensureVoiceoverScriptSession(websocketConnection, token)
  }, [token, websocketConnection])

  return { supported, token, isChecking, requestParams }
}
